import { Button, FieldList, Link, Radio, tokens, Text } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useLayoutEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { TargetingRule } from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import {
  MATCH_OPERATION,
  PAGE_TARGETING_RULE_TYPES,
  RULE_TRIGGER_TYPE,
} from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import { useTranslation, useTranslationWithPrefix } from '@hotjar/translations';
import { PagesTargetingModal } from './PagesTargetingModal';
import { PageTargetingRule } from './PageTargetingRule';

const StyledRadio = styled(Radio)`
  margin-bottom: 0 !important;
`;

const StyledButton = styled(Button)`
  width: auto;
`;

const StyledFieldList = styled(FieldList)`
  margin-top: ${tokens.spacer.x3};
  margin-bottom: ${tokens.spacer.x2};
  width: auto;
`;

const FieldListWithSeparator = styled(StyledFieldList)`
  & > div {
    gap: 10px;
  }
`;

const TargetingSeparator = styled.div`
  ${tokens.text('caption')}
  color: rgba(0,0,0,0.6);
`;

const SpecificPages = styled.div`
  margin-left: ${tokens.spacer.x2};
  width: auto;
`;

export enum TARGETING_MODE {
  ALL = 'all',
  SPECIFIC = 'specific',
}

function getRulesWithUUIDsForPredicate(
  targetingRules: TargetingRule[] | undefined,
  predicate: (rule: TargetingRule) => boolean,
): TargetingRule[] {
  const rules =
    targetingRules !== undefined
      ? targetingRules.filter(
          (rule) => predicate(rule) && PAGE_TARGETING_RULE_TYPES.includes(rule.component),
        )
      : [];
  // the uiuuid is used for iterating over in a stable manner. There's no safe property in the model that could be used,
  // and the index is not suitable as we can remove elements from array
  rules.forEach((rule) => {
    if (!rule.uiUuid) {
      rule.uiUuid = uuid();
    }
  });
  return rules;
}

export interface PagesTargetingProps {
  pagesTargeting?: TARGETING_MODE;
  onChange: (newValue: TARGETING_MODE) => void;
  onAddRule: (newRule: TargetingRule) => void;
  onRemoveRule: (rule: TargetingRule) => void;
  targetingRules: TargetingRule[] | undefined;
  onUpsell: () => void;
  autoValidate: boolean;
  entityType: 'survey' | 'widget' | 'invitation';
}

export function PagesTargeting({
  pagesTargeting,
  onChange,
  onAddRule,
  onRemoveRule,
  targetingRules,
  onUpsell,
  autoValidate,
  entityType,
}: PagesTargetingProps): JSX.Element {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.targeting.page');
  const { t: commonT } = useTranslation('common');
  const [isModalOpen, setModalState] = useState(false);
  const [isAutoValidating, setIsAutoValidating] = useState(autoValidate);

  const targetingLabels: Record<TARGETING_MODE, string> = {
    [TARGETING_MODE.ALL]: t('options.all'),
    [TARGETING_MODE.SPECIFIC]: t('options.specific.title'),
  };

  function addRule(negative: boolean) {
    onAddRule({
      negate: negative,
      // eslint-disable-next-line camelcase
      match_operation: MATCH_OPERATION.SIMPLE,
      component: RULE_TRIGGER_TYPE.URL,
      pattern: '',
      uiUuid: uuid(),
    });
  }

  const positiveRules = useMemo(
    () => getRulesWithUUIDsForPredicate(targetingRules, (rule) => !rule.negate),
    [targetingRules],
  );

  const negativeRules = useMemo(
    () => getRulesWithUUIDsForPredicate(targetingRules, (rule) => rule.negate),
    [targetingRules],
  );

  // enable auto validation if the existing rules contain errors
  // so that the errors are shown when switching back from 'All pages'
  useLayoutEffect(() => {
    setIsAutoValidating(
      (targetingRules !== undefined && targetingRules.some((rule) => rule.error)) || autoValidate,
    );
  }, [targetingRules, autoValidate]);

  return (
    <>
      <StyledFieldList title={t('title')} helperText={t('description', { entityType })} size="m">
        {Object.keys(targetingLabels).map((targeting) => (
          <StyledRadio
            key={targeting}
            isChecked={pagesTargeting === targeting}
            id={`page_targeting_${targeting}`}
            name={targeting}
            onChange={() => onChange(targeting as TARGETING_MODE)}
            value={targeting}
            labelContent={targetingLabels[targeting as TARGETING_MODE]}
            isInvalid={isAutoValidating && !pagesTargeting}
          />
        ))}
        {isAutoValidating && !pagesTargeting && (
          <Text textColor="text_error">{t('validation')}</Text>
        )}
      </StyledFieldList>
      {pagesTargeting === TARGETING_MODE.SPECIFIC && (
        <SpecificPages>
          <FieldListWithSeparator
            title={t('options.specific.include.title', { entityType })}
            helperText={
              <>
                {t('options.specific.include.description')}{' '}
                <Link kind="inline" onClick={() => setModalState(true)}>
                  {t('options.specific.include.link')}
                </Link>
              </>
            }
          >
            {positiveRules.map((rule, index) => (
              <>
                {index !== 0 && (
                  <TargetingSeparator>{commonT('or').toLocaleUpperCase()}</TargetingSeparator>
                )}
                <PageTargetingRule
                  key={rule.uiUuid}
                  onRemove={() => onRemoveRule(rule)}
                  canRemove={positiveRules.length > 1}
                  includeEvents
                  rule={rule}
                  onUpsell={onUpsell}
                  autoValidate={isAutoValidating}
                />
              </>
            ))}
          </FieldListWithSeparator>
          <StyledButton
            title={t('options.specific.include.add')}
            icon="plus"
            kind="secondary"
            onClick={() => addRule(false)}
          >
            {t('options.specific.include.add')}
          </StyledButton>
          <FieldListWithSeparator
            title={t('options.specific.exclude.title', { entityType })}
            helperText={t('options.specific.exclude.description', { entityType })}
          >
            {negativeRules.map((rule, index) => (
              <>
                {index !== 0 && (
                  <TargetingSeparator>{commonT('or').toLocaleUpperCase()}</TargetingSeparator>
                )}
                <PageTargetingRule
                  key={rule.uiUuid}
                  onRemove={() => onRemoveRule(rule)}
                  canRemove
                  rule={rule}
                  onUpsell={onUpsell}
                  autoValidate={isAutoValidating}
                />
              </>
            ))}
          </FieldListWithSeparator>
          <StyledButton
            title={t('options.specific.exclude.add')}
            icon="plus"
            kind="secondary"
            onClick={() => addRule(true)}
          >
            {t('options.specific.exclude.add')}
          </StyledButton>
        </SpecificPages>
      )}
      <PagesTargetingModal isModalOpen={isModalOpen} onModalClose={() => setModalState(false)} />
    </>
  );
}
